/**
 * UpdatePositChatBot V1 - Enhanced Intelligence
 * 
 * Same UI as V0 but with enhanced backend intelligence for domain detection,
 * adaptive questioning, progress tracking, and insight recognition.
 */

import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Message, FileAttachment } from "@/types/posit";
import { useUpdatePositChatV1 } from "./useUpdatePositChatV1";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import ChatContainer from "@/components/chat/interface/ChatContainer";
import { useChatInterface } from "@/contexts/ChatInterfaceContext";

interface UpdatePositChatBotV1Props {
  isNewPosit?: boolean;
  positId?: string;
  conversationId?: string | null;
  lazyLoadMessages?: boolean;
  positTitle?: string;
}

const UpdatePositChatBotV1 = ({ 
  isNewPosit, 
  positId, 
  conversationId,
  lazyLoadMessages = false,
  positTitle
}: UpdatePositChatBotV1Props) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isInitializing, setIsInitializing] = useState(true);
  const hasInitialized = useRef(false);
  
  const {
    setCurrentPosit,
    clearMessages,
    addMessage,
    setIsLoading,
    setWelcomeMode
  } = useChatInterface();

  const {
    messages,
    loading,
    content,
    setContent,
    handleQuery,
  } = useUpdatePositChatV1(user, isNewPosit, positId, conversationId, lazyLoadMessages);

  // Set up the current posit in context for edit mode
  useEffect(() => {
    if (positId) {
      const editPosit = {
        id: positId,
        title: isNewPosit ? "New Posit Creation" : (positTitle || "Posit Update"),
        content: '',
        summary: '',
        privacy: 'private' as const,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_id: user?.id || '',
        response_type: 'assistant' as const
      };
      setCurrentPosit(editPosit);
    }
  }, [positId, isNewPosit, user?.id, setCurrentPosit, positTitle]);

  // Sync messages with context
  useEffect(() => {
    if (messages.length === 0) {
      clearMessages();
      setWelcomeMode(true);
    } else {
      clearMessages();
      messages.forEach(message => {
        addMessage(message);
      });
      setWelcomeMode(false);
    }
  }, [messages, clearMessages, addMessage, setWelcomeMode]);

  // Sync loading state
  useEffect(() => {
    setIsLoading(loading);
  }, [loading, setIsLoading]);

  // Initialize the component
  useEffect(() => {
    if (!user || hasInitialized.current) return;
    
    const initializeComponent = async () => {
      try {
        setIsInitializing(true);
        hasInitialized.current = true;
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to initialize the chat interface."
        });
      } finally {
        setIsInitializing(false);
      }
    };

    initializeComponent();
  }, [user, toast]);

  const handleAsyncSubmit = async (content: string, attachments: FileAttachment[] = []) => {
    try {
      await handleQuery(content, attachments);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error", 
        description: "Failed to process your request. Please try again."
      });
    }
  };

  if (isInitializing) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Initializing chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <ChatContainer 
        onSendMessage={handleAsyncSubmit}
        externalContent={content}
        onContentChange={setContent}
      />
    </div>
  );
};

export default UpdatePositChatBotV1; 